import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
/* Helpers and Selectors */
import { selectUserAddress, selectUserAddressShort, selectIsApproved } from "@/store/user/selectors";
import { refreshWallet } from "@/helpers/wallet_store_helper";
import { addRemoveSub } from "@/helpers/store_helper";
import { checkVisibility } from "@/helpers/ensuro_helper";
import { makeWeTokenCalls } from "@/helpers/wetoken_helper";
import * as etkActionTypes from "@store/eTokens/actionTypes";
/* Store */
import { selectEthCallMultiple } from "@ensuro/ethereum-store";
/* Config */
import {
  SET_CHAIN_ID,
  SET_PROVIDER,
  SET_USER,
  SET_KYC_STATUS_FROM_IFRAME,
  SET_NOTIFICATIONS,
} from "@store/user/actionTypes";
import { chain, kycIframe, currency, images } from "@/config";
import { trackWalletConnect, trackWalletConnectFailed } from "@/helpers/analytics_helper";
/* i18n and Redux */
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector, connect } from "react-redux";
/* wallet config */
import {
  Blobbie,
  ConnectButton as ThirdwebConnectButton,
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
  useActiveWalletConnectionStatus,
} from "thirdweb/react";
import { EIP1193 } from "thirdweb/wallets";
import { client, thirdwebChain, connectionWallets, appTheme } from "./wallet";
import { createTrackWalletProvider, trackWalletName } from "./TrackWallet";

import KycIframeModal from "@/components/Modal/KycIframeModal";
import "./style.scss";

const ConnectWallet = (props) => {
  const { t, user, isApproved } = props;
  const userAddress = selectUserAddress(user);
  const dispatch = useDispatch();
  const account = useActiveAccount();
  const activeWallet = useActiveWallet();
  const connectionStatus = useActiveWalletConnectionStatus();
  const chainInfo = useActiveWalletChain();
  const [showLinkWallet, setShowLinkWallet] = useState(false);
  const [isTrackMode, setIsTrackMode] = useState(false);
  const iframeOrigin = kycIframe.url ? new URL(kycIframe.url).origin : null;

  const trackedSession = useRef(false);

  const provider = useMemo(
    () => (activeWallet ? EIP1193.toProvider({ wallet: activeWallet, chain: thirdwebChain, client }) : null),
    [activeWallet]
  );

  const eTokens = useSelector((state) => state.ETokensReducer.eTokens);

  const isNetworkMismatch = !!account && chainInfo?.id != null && Number(chainInfo.id) !== chain.id;

  const visibleETokens = useMemo(() => eTokens.filter((etk) => checkVisibility(etk, "hide")), [eTokens]);

  const wrapperEntries = useMemo(
    () => visibleETokens.filter((etk) => etk.custom_data?.wrapper?.address),
    [visibleETokens]
  );

  const wrapperCalls = useMemo(
    () => (userAddress ? wrapperEntries.flatMap((etk) => makeWeTokenCalls(etk.custom_data.wrapper, userAddress)) : []),
    [wrapperEntries, userAddress]
  );

  useEffect(() => {
    if (eTokens.length === 0) dispatch({ type: etkActionTypes.GET_ETOKENS });
  }, [eTokens.length, dispatch]);

  useEffect(() => {
    if (!userAddress || !wrapperCalls.length) return;
    return addRemoveSub(dispatch, "connectWalletWrappers", [], wrapperCalls);
  }, [dispatch, userAddress, wrapperCalls]);

  const wrapperBalances = useSelector((state) =>
    wrapperCalls.length ? selectEthCallMultiple(state.EthereumReducer, wrapperCalls) : []
  );

  const connectTrackWallet = useCallback(async () => {
    try {
      const provider = await createTrackWalletProvider();
      const accounts = await provider.request({ method: "eth_requestAccounts" });
      setIsTrackMode(true);
      refreshWallet(dispatch, provider, accounts[0], chain.id, false);
      trackWalletConnect(trackWalletName, chain.name);
    } catch (error) {
      trackWalletConnectFailed(undefined, error?.message || "unknown_error");
    }
  }, [dispatch]);

  // Sync the thirdweb wallet into the Redux store (standard wallets)
  useEffect(() => {
    if (!account?.address || !provider || chainInfo?.id == null) return;
    refreshWallet(dispatch, provider, account.address, Number(chainInfo.id), true);
    if (!trackedSession.current) {
      trackedSession.current = true;
      trackWalletConnect(activeWallet?.id ?? "thirdweb", chain.name);
    }
  }, [account?.address, provider, chainInfo?.id, dispatch, activeWallet]);

  // Auto-reconnect the Track Wallet when there is no thirdweb session and an address was stored
  useEffect(() => {
    if (connectionStatus !== "disconnected" || isTrackMode) return;
    const stored = window.localStorage.getItem("trackWalletAddress");
    if (stored && stored !== "null") connectTrackWallet();
  }, [connectionStatus, isTrackMode, connectTrackWallet]);

  // Listen for kyc:messages from the KYC iframe (any iframe on the page)
  useEffect(() => {
    if (!userAddress) return;

    const onMessage = (event) => {
      if (event.origin === window.location.origin) return;
      if (!kycIframe.allowAnyOrigin && event.origin !== iframeOrigin) return;

      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "kyc:messages") {
        dispatch({
          type: SET_NOTIFICATIONS,
          messages: data.messages ?? [],
          unreadCount: data.unreadCount ?? 0,
        });
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [dispatch, userAddress, iframeOrigin]);

  // Tears down every trace of the current session. Runs both when disconnecting from
  // thirdweb's details modal and when leaving Track Wallet, which has no modal.
  const clearSession = useCallback(() => {
    setIsTrackMode(false);
    window.localStorage.removeItem("trackWalletAddress");
    dispatch({ type: SET_PROVIDER, payload: null });
    dispatch({ type: SET_USER, payload: null, canTransact: false });
    dispatch({ type: SET_CHAIN_ID, payload: null });
    dispatch({ type: SET_KYC_STATUS_FROM_IFRAME, isApproved: false });
    dispatch({ type: SET_NOTIFICATIONS, messages: [], unreadCount: 0 });
  }, [dispatch]);

  const supportedTokens = useMemo(() => {
    const tokens = [];
    if (currency.address) {
      tokens.push({ address: currency.address, name: currency.name, symbol: currency.name });
    }

    const callsPerWrapper = wrapperEntries.length ? wrapperCalls.length / wrapperEntries.length : 0;

    wrapperEntries.forEach((etk, i) => {
      const wrapper = etk.custom_data.wrapper;
      if (wrapperBalances[i * callsPerWrapper]?.value > 0) {
        tokens.push({
          address: wrapper.address,
          name: wrapper.name,
          symbol: wrapper.symbol,
          icon: images.defaultEtkLogo,
        });
      }
    });

    visibleETokens.forEach((etk) => {
      if (!etk.address || !etk.symbol) return;
      tokens.push({
        address: etk.address,
        name: etk.name,
        symbol: etk.symbol,
        icon: etk.logo || images.defaultEtkLogo,
      });
    });

    return tokens.length ? { [chain.id]: tokens } : undefined;
  }, [visibleETokens, wrapperEntries, wrapperCalls.length, wrapperBalances]);

  const thirdwebProps = {
    client,
    chain: thirdwebChain,
    wallets: connectionWallets,
    theme: appTheme,
    showAllWallets: false,
    connectModal: { size: "compact", showThirdwebBranding: false },
    detailsButton: currency.address ? { displayBalanceToken: { [chain.id]: currency.address } } : undefined,
    supportedTokens,
    onDisconnect: clearSession,
  };

  return (
    <div className="d-flex align-items-center header-buttons" style={{ gap: 8 }}>
      {!isTrackMode && !isNetworkMismatch && <ThirdwebConnectButton {...thirdwebProps} />}
      {!userAddress && !isTrackMode && (
        <button
          type="button"
          className="btn header-item waves-effect font-size-16"
          onClick={connectTrackWallet}
          aria-label={t("Track Wallet")}
          title={t("Track Wallet")}
        >
          <i className="uil uil-search" />
        </button>
      )}
      {isTrackMode && userAddress && (
        <>
          <div className="wallet-track">
            <Blobbie address={userAddress} size={35} className="wallet-track__avatar" />
            <span className="wallet-track__info">
              <span className="wallet-track__address">{selectUserAddressShort(user)}</span>
              <span className="wallet-track__label">{t("Track Wallet")}</span>
            </span>
          </div>
          <button
            type="button"
            className="btn header-item waves-effect font-size-16"
            onClick={clearSession}
            aria-label={t("Disconnect")}
            title={t("Disconnect")}
          >
            <i className="uil uil-sign-out-alt" />
          </button>
        </>
      )}
      {!isTrackMode && userAddress && isApproved && (
        <button
          type="button"
          className="btn header-item waves-effect font-size-16"
          onClick={() => setShowLinkWallet(true)}
          aria-label={t("Link Wallet")}
          title={t("Link Wallet")}
        >
          <i className="uil uil-link" />
        </button>
      )}
      <KycIframeModal show={showLinkWallet} onHide={() => setShowLinkWallet(false)} mode="link" />
    </div>
  );
};

ConnectWallet.propTypes = {
  t: PropTypes.any,
  user: PropTypes.object,
  isApproved: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  user: state.UserReducer,
  isApproved: selectIsApproved(state.UserReducer),
});

export default withTranslation()(connect(mapStateToProps)(ConnectWallet));
